import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {CardContent, CardActionArea, Avatar, Chip, Typography, Stack, Divider} from 'views/components';
import {Translate, translate} from 'views/module';
import {LocationOn, Translate as TranslateIcon, Work, School, People, Subject} from 'views/icons'; // mui  icons here

import {simplifyQuestions} from 'utils/user_bio';
import moment from 'moment';
import contrast from 'css/Contrast';

const Card = (props) => {
  const {item} = props;

  const bio = simplifyQuestions(item.bio?.questions ?? {});

  const firstName = (bio?.firstName || '').trim();
  const lastName = (bio?.lastName || '').trim();
  const nameFromBio = [firstName, lastName].filter(Boolean).join(' ');
  const caregiverName = (item?.caregiverName || nameFromBio || translate('caregiver.CaregiverList.DefaultName')).trim();

  const city = bio?.city || '';
  const province = bio?.province || '';
  const locationText = [city, province].filter(Boolean).join(', ');

  const profileImg = bio?.profilePhoto?.[0]?.src;

  const languages = React.useMemo(() => {
    const arr = Array.isArray(bio?.languages) ? bio.languages : [];
    return arr.reduce((acc, language) => {
      const name = (language?.name || '').trim();
      if (!name) return acc;

      const fluency = (language?.fluency || '').trim();

      const localizedName = translate(`caregiver.CaregiverBiography.Languages.AvailableLanguages.${name}`);
      const languageLabel = localizedName !== 'Missing translation' ? localizedName : name;

      let fluencyLabel = '';
      if (fluency) {
        const localizedFluency = translate(`caregiver.CaregiverBiography.Languages.Fluency.${fluency}`);
        fluencyLabel = localizedFluency !== 'Missing translation' ? localizedFluency : fluency;
      }

      acc.push(fluencyLabel ? `${languageLabel} (${fluencyLabel})` : languageLabel);
      return acc;
    }, []);
  }, [bio]);

  const shortBio = (bio?.shortBio || '').trim();

  const workHistory = Array.isArray(bio?.workHistory) ? bio.workHistory : [];
  const yearsExperience = React.useMemo(() => {
    const dates = [];
    workHistory.forEach((w) => {
      if (w?.startDate) dates.push(new Date(w.startDate));
    });
    if (!dates.length) return undefined;
    const earliest = new Date(Math.min.apply(null, dates.map((d) => d.getTime())));
    const now = new Date();
    const diffYears = Math.max(
        0,
        Math.floor((now - earliest) / (1000 * 60 * 60 * 24 * 365.25)),
    );
    return diffYears || undefined;
  }, [workHistory]);

  const certificates = Array.isArray(bio?.certificates) ? bio.certificates : [];
  const certSummary = React.useMemo(() => {
    const primaryCertificate = certificates?.[0];
    if (!primaryCertificate) return undefined;

    const {type, issuingOrganization, dateObtained, city} = primaryCertificate;

    let certificateType = type ?
      translate(`caregiver.CaregiverBiography.Certificate.AvailableCertifications.${type}`) :
      undefined;
    if (certificateType === 'Missing translation') certificateType = type;

    let obtainedYear;
    const parsedDate = dateObtained ? moment(dateObtained) : undefined;
    if (parsedDate?.isValid()) {
      obtainedYear = parsedDate.format('YYYY');
    }

    const summary = [certificateType, issuingOrganization, city, obtainedYear]
        .filter(Boolean)
        .join(' \u2022 ');

    return summary || undefined;
  }, [certificates]);

  const clients = Object.values(item?.clients || {});
  const clientLine = React.useMemo(() => {
    if (!clients.length) return undefined;

    const names = clients
        .map((c) => (c?.clientName || '').trim())
        .filter(Boolean)
        .join(', ');

    return translate('caregiver.CaregiverList.Client', {names});
  }, [clients]);

  const status = item.status;
  const statusTitle = translate(`caregiver.Status.${status}.Title`);
  const statusColor = translate(`caregiver.Status.${status}.Color`);
  const statusTextColor = contrast(statusColor);

  return (
    <CardActionArea
      to={`/caregiver/${item?.caregiverRn}`}
      sx={{
        'textAlign': 'left',
        'transition': 'all 180ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        },
        'borderRadius': 3,
        'boxShadow': '0 2px 8px rgba(0,0,0,0.06)',
        'backgroundColor': 'background.paper',
      }}
    >
      <CardContent sx={{py: {xs: 1.25, sm: 2.5}, px: {xs: 1.75, sm: 2.5}}}>
        <Stack spacing={1}>
          {/* Header row: avatar + name + status */}
          <Stack direction="row" spacing={{xs: 1.25, sm: 1.75}} alignItems="center" >
            <Avatar
              alt={caregiverName}
              src={profileImg}
              sx={{
                width: {xs: 48, sm: 56},
                height: {xs: 48, sm: 56},
                border: '2px solid white',
                boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
              }}
            />

            <Stack spacing={0.25} flex={1} minWidth={0}>
              <Stack direction='row' alignItems={'center'} spacing={1}>
                <Chip
                  label={statusTitle}
                  sx={{
                    backgroundColor: statusColor,
                    color: statusTextColor,
                  }}
                  size="small"
                />
                <Typography
                  variant="h6"
                  component="div"
                  sx={{fontWeight: 700, lineHeight: 1.25, fontSize: {xs: '1rem', sm: '1.05rem'}}}
                  noWrap={false}
                >
                  {caregiverName}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {
                  !!locationText &&
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{flexWrap: 'wrap'}}>
                    <LocationOn sx={{fontSize: 16, color: 'text.secondary'}} />
                    <Typography variant="body2" color="text.secondary">
                      {locationText}
                    </Typography>
                  </Stack>
                }
                {
                  !!languages.length &&
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{flexWrap: 'wrap'}}
                  >
                    <TranslateIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                    {
                      languages.slice(0, 3).map((l, idx) => (
                        <Chip
                          key={idx}
                          label={`${l}`}
                          size="small"
                          sx={{
                            borderRadius: '16px',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            fontWeight: 500,
                            maxWidth: {xs: '100%', sm: 'none'},
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        />
                      ))
                    }
                    {
                      languages.length > 3 &&
                      <Chip
                        label={translate('caregiver.CaregiverList.Languages.Extra', {
                          count: languages.length - 3,
                        })}
                        size="small"
                        sx={{
                          borderRadius: '16px',
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          fontWeight: 500,
                        }}
                      />
                    }
                  </Stack>
                }
              </Stack>
            </Stack>
          </Stack>

          <Divider sx={{my: 1.25}} />

          {/* Summary row: role/cert + years + client count */}
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={{xs: 0.75, sm: 1.25}}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            flexWrap="wrap"
          >
            {
              certSummary &&
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <School sx={{fontSize: 16, transform: 'translateY(2px)'}} />
                <Typography variant="body2" color="text.primary" fontWeight={500} noWrap={false}>
                  {certSummary}
                </Typography>
              </Stack>
            }
            {
              yearsExperience !== undefined &&
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Work sx={{fontSize: 16, color: 'text.secondary', transform: 'translateY(2px)'}} />
                <Typography variant="body2" color="text.secondary" noWrap={false}>
                  <Translate
                    expression={`caregiver.CaregiverList.YearsExperience`}
                    substitute={{count: yearsExperience}}
                  />
                </Typography>
              </Stack>
            }
          </Stack>

          {
            !!shortBio &&
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
            >
              <Subject sx={{fontSize: 16, transform: 'translateY(2px)'}} />
              <Typography
                variant="body2"
                color="text.primary"
                noWrap={false}
              >
                {shortBio}
              </Typography>
            </Stack>
          }

          {
            !!clients.length &&
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
            >
              <People sx={{fontSize: 16, color: 'text.secondary', transform: 'translateY(2px)'}} />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{fontWeight: 600}}
                noWrap={false}
              >
                {clientLine}
              </Typography>
            </Stack>
          }
        </Stack>
      </CardContent>
    </CardActionArea>
  );
};

Card.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(Card));
